import { useState, useEffect } from "react";
import { Save, Loader } from "lucide-react";
import TeamColorsPicker from "../../components/TeamColorsPicker";
import ControlField from "../../components/team/ControlField";
import { TeamSidePanel } from "../../components/team/TeamSidePanel";
import GamePlayer from "../../components/GamePlayer";
import { api } from "../../api/clientInstance";
import { serializeStatements } from "../../components/tactic/tacticModel";
import { playerFromBackend, playerToBackend, normalizeTactic } from "../../utils/teamModel";
import grass from "../../assets/textures/grass2.jpg";
import "./AdminPage.css";

// Passive error-elevation channel (Docs/error-elevation.txt): open alerts
// raised by the backend alert(), with a resolve button.
function AlertsSection() {
  const [data,  setData]  = useState(null);
  const [error, setError] = useState("");
  const load = () => api("/admin/alerts").then(setData).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => {
    await api(`/admin/alerts/${id}/resolve`, { method: "POST" });
    load();
  };
  return (
    <div className="admin-section">
      <h3 className="admin-section-title">⚠ Alerts{data?.open_count ? ` (${data.open_count})` : ""}</h3>
      {error && <p className="admin-error">{error}</p>}
      {data && data.alerts.length === 0 && <p className="admin-loading">No open alerts.</p>}
      {data && data.alerts.map((a) => (
        <div key={a.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0", borderBottom: "1px solid rgba(0,0,0,.1)" }}>
          <span style={{ fontWeight: 700, fontSize: 12, minWidth: 64, color: a.severity === "CRITICAL" ? "#d12b2b" : "#c77700" }}>{a.severity}</span>
          <div style={{ flex: 1 }}>
            <div>{a.message}{a.count > 1 ? ` ×${a.count}` : ""}</div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>{a.key} · {a.last_seen ? a.last_seen.replace("T", " ").slice(0, 19) : ""}{a.context ? ` · ${JSON.stringify(a.context)}` : ""}</div>
          </div>
          <button onClick={() => resolve(a.id)} style={{ cursor: "pointer" }}>Resolve</button>
        </div>
      ))}
    </div>
  );
}

function PlatformStats() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    api("/admin/stats")
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Platform Stats</h3>
      {loading && <p className="admin-loading">…</p>}
      {error   && <p className="admin-error">{error}</p>}
      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat"><span className="admin-stat-value">{stats.total_users}</span><span className="admin-stat-label">Users (registered)</span></div>
          <div className="admin-stat"><span className="admin-stat-value">{stats.total_teams}</span><span className="admin-stat-label">Teams</span></div>
          <div className="admin-stat"><span className="admin-stat-value">{stats.total_matches}</span><span className="admin-stat-label">Matches total</span></div>
          <div className="admin-stat"><span className="admin-stat-value">{stats.matches_today}</span><span className="admin-stat-label">Matches today</span></div>
          <div className="admin-stat"><span className="admin-stat-value">{stats.matches_week}</span><span className="admin-stat-label">Matches this week</span></div>
          <div className="admin-stat"><span className="admin-stat-value">{stats.new_users_week}</span><span className="admin-stat-label">New users this week</span></div>
          <div className="admin-stat admin-stat--guest"><span className="admin-stat-value">{stats.live_guests}</span><span className="admin-stat-label">Guests (live)</span></div>
          <div className="admin-stat admin-stat--guest"><span className="admin-stat-value">{stats.guests_tried_week}</span><span className="admin-stat-label">Guests tried (7d)</span></div>
          <div className="admin-stat admin-stat--guest"><span className="admin-stat-value">{stats.guests_registered_week}</span><span className="admin-stat-label">Guests registered (7d)</span></div>
        </div>
      )}
    </div>
  );
}

// Two entry doors (trys / registros) are separate roots, NOT a single chain —
// there are direct signups that never went through "Try it", so registros/trys
// is not a conversion. Each root has its own children measured against it.
const FUNNEL_STAGES = [
  { key: "trys",           label: "Trys (Try it)",     base: null,         root: true },
  { key: "trys_converted", label: "› se registró",     base: "trys" },
  { key: "registros",      label: "Registros (total)", base: null,         root: true },
  { key: "worked",         label: "› equipo trabajado", base: "registros" },
  { key: "jugaron",        label: "› jugó partido",    base: "registros" },
  { key: "volvieron",      label: "› volvió otro día", base: "registros" },
];

function funnelPct(n, d) {
  if (!d) return null;
  return Math.round((n / d) * 100);
}

function AcquisitionFunnel() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    api("/admin/funnel")
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const maxDaily = data
    ? Math.max(1, ...data.daily.flatMap((d) => [d.trys, d.registros]))
    : 1;

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Acquisition Funnel</h3>
      {loading && <p className="admin-loading">…</p>}
      {error   && <p className="admin-error">{error}</p>}
      {data && (
        <>
          <table className="funnel-table">
            <thead>
              <tr>
                <th className="funnel-stage-col" />
                {data.windows.map((w) => <th key={w.key}>{w.label}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr className="funnel-row funnel-row--muted">
                <td>Visitas (humanos)</td>
                {data.windows.map((w) => <td key={w.key}>N/D<sup>*</sup></td>)}
              </tr>
              {FUNNEL_STAGES.map((st) => (
                <tr
                  key={st.key}
                  className={`funnel-row${st.root ? " funnel-row--root" : " funnel-row--sub"}`}
                >
                  <td>{st.label}</td>
                  {data.windows.map((w) => {
                    const val = w.funnel[st.key];
                    const p   = st.base ? funnelPct(val, w.funnel[st.base]) : null;
                    return (
                      <td key={w.key}>
                        <span className="funnel-val">{val}</span>
                        {p !== null && <span className="funnel-pct">{p}%</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="funnel-foot">
            <sup>*</sup> Visitas humanas aún sin medir (beacon pendiente). El resto son
            acciones reales en BD; táctica/partido cuentan por existencia, no por clics.
          </p>

          <div className="funnel-source">
            <span className="funnel-source-label">Registros por origen:</span>
            {data.by_source.map((s) => (
              <span key={s.source} className="funnel-chip">{s.source} · {s.count}</span>
            ))}
          </div>

          <div className="funnel-spark">
            <span className="funnel-source-label">Últimos 14 días</span>
            <div className="funnel-spark-bars">
              {data.daily.map((d) => (
                <div
                  key={d.date}
                  className="funnel-spark-day"
                  title={`${d.date}: ${d.trys} trys · ${d.registros} registros`}
                >
                  <div className="funnel-spark-stack">
                    <div className="funnel-spark-bar funnel-spark-bar--try"
                         style={{ height: `${(d.trys / maxDaily) * 100}%` }} />
                    <div className="funnel-spark-bar funnel-spark-bar--reg"
                         style={{ height: `${(d.registros / maxDaily) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="funnel-spark-legend">
              <span className="funnel-chip funnel-chip--try">trys</span>
              <span className="funnel-chip funnel-chip--reg">registros</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function UserManagement() {
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editName,    setEditName]    = useState("");
  const [editTeam,    setEditTeam]    = useState("");
  const [saving,      setSaving]      = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error,    setError]    = useState("");

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setError("");
    try {
      const data = await api(`/admin/users?name=${encodeURIComponent(query.trim())}`);
      setResults(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const selectUser = (u) => {
    setSelected(u);
    setEditName(u.name);
    setEditTeam(u.team_name ?? "");
    setConfirmReset(false);
    setConfirmDeactivate(false);
    setError("");
  };

  const refresh = (updated) => {
    setSelected(updated);
    setEditName(updated.name);
    setEditTeam(updated.team_name ?? "");
    setResults((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
  };

  const handleRenameUser = async () => {
    setSaving("username");
    setError("");
    try {
      const updated = await api(`/admin/users/${selected.id}/username`, {
        method: "PATCH",
        body: JSON.stringify({ name: editName }),
      });
      refresh(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleRenameTeam = async () => {
    setSaving("team");
    setError("");
    try {
      const updated = await api(`/admin/users/${selected.id}/team-name`, {
        method: "PATCH",
        body: JSON.stringify({ name: editTeam }),
      });
      refresh(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleDeactivate = async () => {
    setSaving("deactivate");
    setConfirmDeactivate(false);
    setError("");
    try {
      await api(`/admin/users/${selected.id}`, { method: "DELETE" });
      setResults((prev) => prev.filter((u) => u.id !== selected.id));
      setSelected(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleVerifyEmail = async () => {
    setVerifying(true);
    setError("");
    try {
      const updated = await api(`/admin/users/${selected.id}/verify-email`, { method: "POST" });
      refresh(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleResetElo = async () => {
    setSaving("elo");
    setError("");
    setConfirmReset(false);
    try {
      const updated = await api(`/admin/users/${selected.id}/reset-elo`, { method: "POST" });
      refresh(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">User Management</h3>

      <div className="admin-search">
        <input
          className="admin-search-input"
          placeholder="Search user by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button className="admin-search-btn" onClick={handleSearch} disabled={searching}>
          {searching ? "…" : "Search"}
        </button>
      </div>

      {results.length > 0 && (
        <ul className="admin-results">
          {results.map((u) => (
            <li
              key={u.id}
              className={`admin-result-item${selected?.id === u.id ? " admin-result-item--active" : ""}`}
              onClick={() => selectUser(u)}
            >
              <span className="admin-result-name">{u.name}</span>
              <span className="admin-result-meta">
                {u.team_name ?? "no team"}{u.elo != null ? ` · ELO ${u.elo}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="admin-error">{error}</p>}

      {selected && (
        <div className="admin-user-editor">
          <div className="admin-user-field">
            <label className="admin-user-label">Username</label>
            <div className="admin-user-input-row">
              <input
                className="admin-user-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <button
                className="admin-user-btn"
                onClick={handleRenameUser}
                disabled={saving === "username" || editName === selected.name}
              >
                {saving === "username" ? <Loader size={14} className="spin" /> : "Save"}
              </button>
            </div>
          </div>

          <div className="admin-user-field">
            <label className="admin-user-label">Team name</label>
            <div className="admin-user-input-row">
              <input
                className="admin-user-input"
                value={editTeam}
                onChange={(e) => setEditTeam(e.target.value)}
                disabled={!selected.team_id}
                placeholder={selected.team_id ? "" : "No team"}
              />
              <button
                className="admin-user-btn"
                onClick={handleRenameTeam}
                disabled={saving === "team" || !selected.team_id || editTeam === selected.team_name}
              >
                {saving === "team" ? <Loader size={14} className="spin" /> : "Save"}
              </button>
            </div>
          </div>

          <div className="admin-user-field">
            <label className="admin-user-label">ELO</label>
            <div className="admin-user-input-row">
              <span className="admin-elo-value">{selected.elo ?? "—"}</span>
              {confirmReset ? (
                <>
                  <button className="admin-user-btn admin-user-btn--danger" onClick={handleResetElo} disabled={saving === "elo"}>
                    {saving === "elo" ? <Loader size={14} className="spin" /> : "Yes, reset"}
                  </button>
                  <button className="admin-user-btn" onClick={() => setConfirmReset(false)}>Cancel</button>
                </>
              ) : (
                <button
                  className="admin-user-btn admin-user-btn--danger"
                  onClick={() => setConfirmReset(true)}
                  disabled={!selected.team_id}
                >
                  Reset to 1000
                </button>
              )}
            </div>
          </div>

          <div className="admin-user-field">
            <label className="admin-user-label">Email</label>
            <div className="admin-user-input-row">
              <span className="admin-elo-value">{selected.email ?? "—"}</span>
              {selected.email_verified ? (
                <span className="admin-verified-badge">✓ verified</span>
              ) : (
                <button
                  className="admin-user-btn"
                  onClick={handleVerifyEmail}
                  disabled={verifying || !selected.email}
                >
                  {verifying ? <Loader size={14} className="spin" /> : "Verify manually"}
                </button>
              )}
            </div>
          </div>

          <div className="admin-user-field">
            <label className="admin-user-label">Account</label>
            <div className="admin-user-input-row">
              {confirmDeactivate ? (
                <>
                  <button className="admin-user-btn admin-user-btn--danger" onClick={handleDeactivate} disabled={saving === "deactivate"}>
                    {saving === "deactivate" ? <Loader size={14} className="spin" /> : "Yes, deactivate"}
                  </button>
                  <button className="admin-user-btn" onClick={() => setConfirmDeactivate(false)}>Cancel</button>
                </>
              ) : (
                <button
                  className="admin-user-btn admin-user-btn--danger"
                  onClick={() => setConfirmDeactivate(true)}
                >
                  Deactivate user
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackSection() {
  const [items,      setItems]      = useState([]);
  const [showViewed, setShowViewed] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  const load = (viewed) => {
    setLoading(true);
    api(`/admin/feedback?show_viewed=${viewed}`)
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(showViewed); }, [showViewed]);

  const handleMarkViewed = async (id) => {
    await api(`/admin/feedback/${id}/viewed`, { method: "PATCH" });
    setItems((prev) => showViewed
      ? prev.map((f) => f.id === id ? { ...f, viewed: true } : f)
      : prev.filter((f) => f.id !== id)
    );
  };

  const handleDelete = async (id) => {
    await api(`/admin/feedback/${id}`, { method: "DELETE" });
    setItems((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div className="admin-section">
      <div className="admin-feedback-header">
        <h3 className="admin-section-title">Feedback</h3>
        <label className="admin-feedback-toggle">
          <input type="checkbox" checked={showViewed} onChange={(e) => setShowViewed(e.target.checked)} />
          Show viewed
        </label>
      </div>
      {loading && <p className="admin-loading">…</p>}
      {error   && <p className="admin-error">{error}</p>}
      {!loading && items.length === 0 && <p className="admin-loading">No feedback yet.</p>}
      <ul className="admin-feedback-list">
        {items.map((f) => (
          <li key={f.id} className={`admin-feedback-item${f.viewed ? " admin-feedback-item--viewed" : ""}`}>
            <div className="admin-feedback-meta">
              <span className="admin-feedback-user">{f.user}</span>
              <span className="admin-feedback-date">{new Date(f.created_at).toLocaleDateString()}</span>
              {!f.viewed && (
                <button className="admin-user-btn" onClick={() => handleMarkViewed(f.id)}>Mark viewed</button>
              )}
              <button className="admin-user-btn admin-user-btn--danger" onClick={() => handleDelete(f.id)}>Delete</button>
            </div>
            <p className="admin-feedback-msg">{f.message}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TeamPicker({ label, onSelect }) {
  const [query,   setQuery]   = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const data = await api(`/admin/teams?name=${encodeURIComponent(query.trim())}`);
      setResults(data);
    } finally {
      setSearching(false);
    }
  };

  const pick = (team) => {
    setSelected(team);
    setResults([]);
    setQuery(team.name);
    onSelect(team);
  };

  return (
    <div className="admin-simulate-picker">
      <label className="admin-user-label">{label}</label>
      <div className="admin-search">
        <input
          className="admin-search-input"
          placeholder="Search team…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); onSelect(null); }}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <button className="admin-search-btn" onClick={search} disabled={searching}>
          {searching ? "…" : "Search"}
        </button>
      </div>
      {results.length > 0 && (
        <ul className="admin-results">
          {results.map((t) => (
            <li key={t.id} className="admin-result-item" onClick={() => pick(t)}>
              <span className="admin-result-name">{t.name}</span>
              <span className="admin-result-meta">{t.owner ?? "system"} · ELO {t.elo}</span>
            </li>
          ))}
        </ul>
      )}
      {selected && <p className="admin-simulate-selected">✓ {selected.name}</p>}
    </div>
  );
}

function SimulateSection() {
  const [localTeam, setLocalTeam] = useState(null);
  const [awayTeam,  setAwayTeam]  = useState(null);
  const [running,   setRunning]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState("");

  const handleSimulate = async () => {
    if (!localTeam || !awayTeam) return;
    setRunning(true);
    setError("");
    setResult(null);
    try {
      const data = await api("/admin/simulate", {
        method: "POST",
        body: JSON.stringify({ local_team_id: localTeam.id, away_team_id: awayTeam.id }),
      });
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Simulate Match</h3>
      <div className="admin-simulate-row">
        <TeamPicker label="Local team" onSelect={setLocalTeam} />
        <span className="admin-simulate-vs">vs</span>
        <TeamPicker label="Away team"  onSelect={setAwayTeam} />
      </div>
      <button
        className="admin-simulate-btn"
        onClick={handleSimulate}
        disabled={running || !localTeam || !awayTeam}
      >
        {running ? <><Loader size={14} className="spin" /> Running…</> : "▶ Simulate"}
      </button>
      {error && <p className="admin-error">{error}</p>}
      {result && (
        <div className="admin-simulate-result">
          <GamePlayer
            frames={result.frames}
            localColor={localTeam?.local_color ?? "#ff0000"}
            awayColor={awayTeam?.local_color ?? "#0000ff"}
            localPlayerNames={[]}
            awayPlayerNames={[]}
            autoPlay
          />
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [players,  setPlayers]  = useState([]);
  const [tactics,  setTactics]  = useState([]);
  const [colors,   setColors]   = useState({ local: "#ff0000", away: "#0000ff" });
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setError("");
    try {
      const data = await api(`/admin/teams?name=${encodeURIComponent(query.trim())}`);
      setResults(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = async (teamId) => {
    setError("");
    try {
      const data = await api(`/admin/teams/${teamId}`);
      setSelected(data);
      setColors({ local: data.local_color, away: data.away_color });
      setPlayers(data.players.map(playerFromBackend));
      setTactics(data.tactics.map(normalizeTactic));
      setSelectedPlayerId(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    try {
      await api(`/admin/teams/${selected.id}`, {
        method: "PUT",
        body: JSON.stringify({ name: selected.name, local_color: colors.local, away_color: colors.away }),
      });
      await api(`/admin/teams/${selected.id}/players`, {
        method: "PUT",
        body: JSON.stringify(players.map(playerToBackend)),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTactic = async (name) => {
    const created = await api(`/admin/teams/${selected.id}/tactics`, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    const tactic = normalizeTactic(created);
    setTactics((prev) => [...prev, tactic]);
    return tactic;
  };

  const handleUpdateTactic = async (id, statements) => {
    await api(`/admin/teams/${selected.id}/tactics/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ tokens: serializeStatements(statements) }),
    });
    setTactics((prev) => prev.map((t) => (t.id === id ? { ...t, statements } : t)));
  };

  const handleDeleteTactic = async (id) => {
    await api(`/admin/teams/${selected.id}/tactics/${id}`, { method: "DELETE" });
    setTactics((prev) => prev.filter((t) => t.id !== id));
  };

  const handleMove          = (id, x, y) => setPlayers((prev) => prev.map((p) => (p.frontendId === id ? { ...p, x, y } : p)));
  const handleToggleStarter = (id) => setPlayers((prev) => prev.map((p) => ({ ...p, isStarter: p.frontendId === id ? !p.isStarter : false })));
  const handleAssignTactic  = (pid, tacticId) => setPlayers((prev) => prev.map((p) => (p.frontendId === pid ? { ...p, tacticId } : p)));
  const handleRenamePlayer  = (pid, name) => setPlayers((prev) => prev.map((p) => (p.frontendId === pid ? { ...p, name } : p)));

  return (
    <div className="admin">
      <h2 className="admin-title">Admin</h2>

      <AlertsSection />
      <PlatformStats />
      <AcquisitionFunnel />
      <FeedbackSection />
      <UserManagement />
      <SimulateSection />

      <div className="admin-section">
      <h3 className="admin-section-title">Team Editor</h3>

      <div className="admin-search">
        <input
          className="admin-search-input"
          placeholder="Search team by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button className="admin-search-btn" onClick={handleSearch} disabled={searching}>
          {searching ? "…" : "Search"}
        </button>
      </div>

      {results.length > 0 && (
        <ul className="admin-results">
          {results.map((t) => (
            <li
              key={t.id}
              className={`admin-result-item${selected?.id === t.id ? " admin-result-item--active" : ""}`}
              onClick={() => handleSelect(t.id)}
            >
              <span className="admin-result-name">{t.name}</span>
              <span className="admin-result-meta">{t.owner ?? "system"} · ELO {t.elo}</span>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="admin-error">{error}</p>}

      {selected && (
        <div className="admin-editor">
          <div className="admin-editor-header">

            <span className="admin-editor-name">{selected.name}</span>
            {selected.owner && <span className="admin-editor-owner">by {selected.owner}</span>}
            <TeamColorsPicker
              colors={{ local: colors.local, away: colors.away }}
              onChange={(type, value) => setColors((prev) => ({ ...prev, [type === "local" ? "local" : "away"]: value }))}
            />
            <button className="team-save-btn admin-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
            </button>
          </div>

          <div className="team-editor-layout">
            <div className="team-field-section">
              <ControlField
                players={players}
                selectedId={selectedPlayerId}
                teamColor={colors.local}
                background={grass}
                onSelect={setSelectedPlayerId}
                onMove={handleMove}
              />
            </div>
            <div className="team-tactics-section">
              <TeamSidePanel
                players={players}
                selectedId={selectedPlayerId}
                tactics={tactics}
                tacticsLoading={false}
                onToggleStarter={handleToggleStarter}
                onAssignTactic={handleAssignTactic}
                onRenamePlayer={handleRenamePlayer}
                onCreate={handleCreateTactic}
                onUpdate={handleUpdateTactic}
                onDelete={handleDeleteTactic}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
